import { ReactComponent as FiRrEye } from 'new/components/icons/fi-rr-eye.svg';
import { Dialog, Tooltip } from '@mui/material';
import { FILE_LIST_MODE_TYPES } from 'js/constants/paginationConstants';
import { IconButton } from 'new/elements/IconButton';
import { ResourceStatsFile } from 'new/hooks/adapters/useResourceStatsFiles';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { Stack } from 'new/elements/Stack';
import { Checkbox } from 'new/elements/Checkbox/Checkbox';
import { FileUtil } from 'new/utils/FileUtil';
import { ReactComponent as RefreshRounded } from 'new/components/icons/refresh.svg';
import { useReingestFiles } from '../ReadStats/hooks/useReingestFiles';
import { useResource } from 'new/components/monitor/ResourceTriage/useResource';
import { FileItemListRow } from './types';
import { ReingestDialogContent } from 'js/components/elements/files/ReingestDialogContent';

interface FileItemsListProps {
  loadMore?: (page: number) => void;
  onFilePreviewClicked?: (row: any) => void;
  resourceType: string;
  resourceId: string;
  mode?: keyof typeof FILE_LIST_MODE_TYPES;
  items: any[];
  connectionCategory: string;
  writeStatus?: string;
  pageCount: number;
  currentPage: number;
}

const extractFileProperties = (file: Partial<FileItemListRow>) => {
  return {
    name: file?.name,
    ingestionStatus: file?.ingestionStatus,
    writeStatus: file?.writeStatus,
    recordCount: file?.recordCount,
    metadata: {
      display_path: file?.metadata?.display_path,
      date: file?.metadata?.date,
      from_email: file?.metadata?.from_email,
      subject: file?.metadata?.subject,
    },
    lastIngested: file?.lastIngested,
    lastWritten: file?.lastWritten,
    size: file?.size,
  };
};

export const FileItemsListActions = (
  props: ResourceStatsFile &
    FileItemsListProps & {
      filters: any;
      mode: string;
      bucket: string;
      onClickPreview: any;
      data_credentials_id?: number;
      isSelected: boolean;
      isMultipleReingestActive?: boolean;
      onSelectFile(checked: boolean, data: any): void;
    },
) => {
  const {
    resourceType,
    resourceId,
    filters,
    mode,
    ingestionStatus,
    writeStatus,
    onClickPreview,
    connectionCategory,
    onSelectFile,
    isSelected,
    isMultipleReingestActive = false,
  } = props;

  const [confirmReingest, setConfirmReingest] = useState(false);
  const { resource } = useResource(resourceType, resourceId);

  const allowedReingest = FileUtil.isReingestAllowed(
    ingestionStatus || writeStatus,
    connectionCategory,
    mode,
  );

  const { handleReingest, loadFileStats } = useReingestFiles({
    resourceId,
    resource,
    sort: filters?.orderby,
    status: filters?.status,
  });

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSelectFile(event.target.checked, props);
    },
    [onSelectFile],
  );

  const file = extractFileProperties(props);
  const openConfirmReingestModal = () => {
    handleReingest([file], {
      onSuccess: () => {
        setConfirmReingest(false);
        loadFileStats(resourceId, resourceType);
      },
    });
  };

  return (
    <Stack
      width="100%"
      paddingRight="8px"
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
    >
      {allowedReingest && !isMultipleReingestActive && (
        <Tooltip title="Reingest">
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              setConfirmReingest(true);
            }}
          >
            <RefreshRounded color="#000000" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Preview">
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            onClickPreview(props)?.();
          }}
        >
          <FiRrEye />
        </IconButton>
      </Tooltip>

      {allowedReingest && isMultipleReingestActive && (
        <Checkbox checked={isSelected} onChange={onChange} />
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        open={confirmReingest}
        PaperProps={{ onClick: (e) => e.stopPropagation() }}
        onClose={() => setConfirmReingest(false)}
      >
        <ReingestDialogContent
          file={file}
          onConfirm={openConfirmReingestModal}
          onClose={() => setConfirmReingest(false)}
        />
      </Dialog>
    </Stack>
  );
};
